import styles from './index.module.scss';
import { Popover } from '@base-ui/react/popover';
import { useCallback } from 'react';
import { NavLink, useLocation } from 'react-router';
import { EPath } from '@/router/path';
import { IconFont } from '@/components/iconfont';
import { Button } from '../button';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '../user-avatar';
import { LoadingIcon } from '../loading-icon';
import { openLink } from '@/utils/browser/open-link';
import { isEnterpriseAdmin } from '@/utils/auth';
import { useSignOut } from '@/hooks/useSignOut';
import { useProfile } from '@/stores/user';

const UserPopupTrigger = ({ userName, userAvatar }: { userName?: string; userAvatar?: string }) => {
  return (
    <Popover.Trigger className={styles.trigger} openOnHover delay={0} closeDelay={200}>
      <UserAvatar className={styles.avatar} size={36} src={userAvatar} />
      <span className={styles.userName}>{userName}</span>
      <IconFont iconName="icon-icon_operation_arrow_down_24" className={styles.triggerIcon} fontSize={16} />
    </Popover.Trigger>
  );
};

export const UserPopup = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const profile = useProfile();
  const { signOut, loading } = useSignOut();
  const logoutHandler = useCallback(() => {
    // 编辑器改走整文档跳转：靠 document unload 触发浏览器原生的未保存提示
    if (pathname === EPath.Editor) {
      openLink(EPath.Logout, false);
      return;
    }
    void signOut();
  }, [pathname, signOut]);

  // 根级 ErrorBoundary 在 protected 分支之外，但它也会渲染 Header，那条路径上 protectLoader
  // 没跑过，store 里就没有 profile
  if (!profile?.userId) return null;

  return (
    <Popover.Root>
      <UserPopupTrigger userName={profile.name} userAvatar={profile.avatar ?? undefined} />
      <Popover.Portal>
        <Popover.Positioner sideOffset={8}>
          <Popover.Popup className={styles.popup}>
            <NavLink
              to={EPath.Workbench}
              reloadDocument={pathname === EPath.Editor}
              className={styles.menuLink}
              onClick={(e) => {
                if (pathname === EPath.Workbench) e.preventDefault();
              }}
            >
              {t('user.loginsignup.programmanage')}
            </NavLink>
            <NavLink
              to={EPath.Account}
              reloadDocument={pathname === EPath.Editor}
              className={styles.menuLink}
              onClick={(e) => {
                if (pathname === EPath.Account) e.preventDefault();
              }}
            >
              {t('user.loginsignup.personalcenter')}
            </NavLink>
            {isEnterpriseAdmin(profile) && (
              <NavLink
                to={EPath.AdminTeam}
                reloadDocument={pathname === EPath.Editor}
                className={styles.menuLink}
                onClick={(e) => {
                  if (pathname === EPath.AdminTeam) e.preventDefault();
                }}
              >
                {t('team.title')}
              </NavLink>
            )}
            <Button
              variant="outline"
              fit
              size="md"
              className={styles.button}
              loadingIcon={<LoadingIcon className={styles.loadingIcon} />}
              loading={loading}
              disabled={loading}
              onClick={logoutHandler}
            >
              {t('user.loginsignup.logout')}
            </Button>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};
